// Hand-built SVG icons with gradients + drop-shadow for a subtle 3D feel.
// No emoji, no unicode glyphs, no external icon fonts: everything drawn here.
import { useId } from 'react';

export function BridgeMark({ size = 36, color = '#2f7a3a', accent = '#6fb04a' }) {
  const baseId = useId();
  const gId = `bm-g-${baseId}`;
  const sId = `bm-s-${baseId}`;
  return (
    <svg width={size} height={size * 0.58} viewBox="0 0 100 58" aria-hidden="true">
      <defs>
        <linearGradient id={gId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor={accent} />
          <stop offset="100%" stopColor={color} />
        </linearGradient>
        <filter id={sId} x="-10%" y="-10%" width="120%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="1.6" floodOpacity="0.18" />
        </filter>
      </defs>
      <g filter={`url(#${sId})`} fill={`url(#${gId})`}>
        <polygon points="0,56 14,8 26,56" />
        <polygon points="22,56 50,0 78,56" />
        <polygon points="74,56 86,8 100,56" />
      </g>
    </svg>
  );
}

export function SunIcon({ size = 28 }) {
  const baseId = useId();
  const gId = `sun-g-${baseId}`;
  const sId = `sun-s-${baseId}`;
  return (
    <svg width={size} height={size} viewBox="0 0 48 48" aria-hidden="true">
      <defs>
        <radialGradient id={gId} cx="50%" cy="40%" r="60%">
          <stop offset="0%" stopColor="#fff6c8" />
          <stop offset="55%" stopColor="#f4c64a" />
          <stop offset="100%" stopColor="#c77a00" />
        </radialGradient>
        <filter id={sId} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="1.2" stdDeviation="1.4" floodOpacity="0.22" />
        </filter>
      </defs>
      <g filter={`url(#${sId})`}>
        {[0, 45, 90, 135, 180, 225, 270, 315].map((deg) => (
          <rect
            key={deg}
            x="22.5" y="2" width="3" height="8" rx="1.5"
            fill="#f4c64a"
            transform={`rotate(${deg} 24 24)`}
          />
        ))}
        <circle cx="24" cy="24" r="11" fill={`url(#${gId})`} />
        <ellipse cx="21" cy="20" rx="4" ry="2" fill="#fff9d8" opacity="0.55" />
      </g>
    </svg>
  );
}

export function CheckIcon({ size = 56 }) {
  const baseId = useId();
  const gId = `chk-g-${baseId}`;
  const sId = `chk-s-${baseId}`;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <linearGradient id={gId} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor="#8fd36a" />
          <stop offset="100%" stopColor="#2f7a3a" />
        </linearGradient>
        <filter id={sId} x="-15%" y="-15%" width="130%" height="140%">
          <feDropShadow dx="0" dy="3" stdDeviation="2.2" floodOpacity="0.22" />
        </filter>
      </defs>
      <g filter={`url(#${sId})`}>
        <circle cx="32" cy="32" r="28" fill={`url(#${gId})`} />
        <ellipse cx="24" cy="20" rx="10" ry="5" fill="#ffffff" opacity="0.18" />
        <path
          d="M18 33 L28 43 L46 22"
          stroke="#ffffff"
          strokeWidth="5"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </g>
    </svg>
  );
}

export function ArrowBackIcon({ size = 18, color = 'currentColor' }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true">
      <path
        d="M15 5 L8 12 L15 19"
        stroke={color}
        strokeWidth="2.2"
        strokeLinecap="round"
        strokeLinejoin="round"
        fill="none"
      />
    </svg>
  );
}

export function UploadCloudIcon({ size = 48 }) {
  const baseId = useId();
  const gId = `uc-g-${baseId}`;
  const sId = `uc-s-${baseId}`;
  return (
    <svg width={size} height={size} viewBox="0 0 64 64" aria-hidden="true">
      <defs>
        <linearGradient id={gId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#bfd4ff" />
          <stop offset="100%" stopColor="#6a85d1" />
        </linearGradient>
        <filter id={sId} x="-10%" y="-10%" width="120%" height="130%">
          <feDropShadow dx="0" dy="2" stdDeviation="1.6" floodOpacity="0.18" />
        </filter>
      </defs>
      <g filter={`url(#${sId})`}>
        <path
          d="M19 46 C10 46 6 39 8 33 C9 26 16 24 18 25 C19 17 27 13 33 16 C39 18 41 24 41 26 C50 25 56 32 53 40 C51 45 46 46 42 46 Z"
          fill={`url(#${gId})`}
        />
        <path
          d="M32 40 L32 24 M24 32 L32 24 L40 32"
          stroke="#ffffff"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
          fill="none"
        />
      </g>
    </svg>
  );
}

export function FileIcon({ size = 22 }) {
  const baseId = useId();
  const gId = `f-g-${baseId}`;
  return (
    <svg width={size} height={size} viewBox="0 0 32 32" aria-hidden="true">
      <defs>
        <linearGradient id={gId} x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#eef2fb" />
          <stop offset="100%" stopColor="#cbd5ec" />
        </linearGradient>
      </defs>
      <path d="M8 3 h12 l6 6 v19 a2 2 0 0 1 -2 2 h-16 a2 2 0 0 1 -2 -2 v-23 a2 2 0 0 1 2 -2 z" fill={`url(#${gId})`} stroke="#a5b3cf" strokeWidth="1" />
      <path d="M20 3 v6 h6" fill="none" stroke="#a5b3cf" strokeWidth="1" />
    </svg>
  );
}
